import { BooleanValue, Schema } from '@coveo/bueno';
import { configuration } from '../../../app/common-reducers.js';
import { getConfigurationInitialState } from '../../../features/configuration/configuration-state.js';
import { prepareForSearchWithQuery } from '../../../features/search/search-actions.js';
import { registerTab, updateActiveTab, } from '../../../features/tab-set/tab-set-actions.js';
import { tabSetReducer as tabSet } from '../../../features/tab-set/tab-set-slice.js';
import { loadReducerError } from '../../../utils/errors.js';
import { requiredEmptyAllowedString, requiredNonEmptyString, validateInitialState, validateOptions, } from '../../../utils/validate-payload.js';
import { buildController, } from '../../controller/headless-controller.js';
const optionsSchema = new Schema({
    expression: requiredEmptyAllowedString,
    id: requiredNonEmptyString,
    clearFiltersOnTabChange: new BooleanValue(),
});
const initialStateSchema = new Schema({
    isActive: new BooleanValue(),
});
/**
 * Creates a `Tab` controller instance.
 *
 * @param engine - The headless engine.
 * @param props - The configurable `Tab` properties.
 * @returns A `Tab` controller instance.
 */
export function buildCoreTab(engine, props) {
    assertIdNotEqualToDefaultOriginLevel2(props.options.id);
    if (!loadTabReducers(engine)) {
        throw loadReducerError;
    }
    const controller = buildController(engine);
    const { dispatch } = engine;
    validateOptions(engine, optionsSchema, props.options, 'buildTab');
    const initialState = validateInitialState(engine, initialStateSchema, props.initialState, 'buildTab');
    const { id, expression } = props.options;
    dispatch(registerTab({ id, expression }));
    const isFirstTab = Object.keys(engine.state.tabSet).length === 1;
    if (isFirstTab) {
        initialState.isActive = true;
    }
    if (initialState.isActive) {
        dispatch(updateActiveTab(id));
    }
    return {
        ...controller,
        select() {
            if (props.options.clearFiltersOnTabChange) {
                dispatch(prepareForSearchWithQuery({
                    q: '',
                    clearFilters: true,
                }));
            }
            dispatch(updateActiveTab(id));
        },
        get state() {
            const isActive = engine.state.tabSet[id]?.isActive;
            return {
                isActive,
            };
        },
    };
}
function loadTabReducers(engine) {
    engine.addReducers({ configuration, tabSet });
    return true;
}
function assertIdNotEqualToDefaultOriginLevel2(id) {
    const defaultOriginLevel2 = getConfigurationInitialState().analytics.originLevel2;
    if (id === defaultOriginLevel2) {
        throw new Error(`The #id option on the Tab controller cannot use the reserved value "${defaultOriginLevel2}". Please specify a different value.`);
    }
}
